import analytics from "web/script/analytics/analytics";
import classnames from "classnames";
import { gettext } from "web/script/modules/django-i18n";
import PropTypes from "prop-types";
import React from "react";
import environment from "web/script/modules/environment";
import LegalAgreement from "web/react/components/legal-agreement/legal-agreement";
import Button from "web/react/components/button/button";
import SocialButton from "web/react/components/social-button/social-button";
import styles from "./email-capture-form.css";

export function EmailCaptureForm({
    captureType,
    className,
    emailAddress,
    emailLabelText,
    formErrors,
    onEmailChange,
    onLegalChange,
    onSubmit,
    privacyPolicyLink,
    submitText,
    submitClass,
    termsAndConditionsLink,
    cookiePolicyLink,
    shortPolicy,
    showSocial,
    hideEmail = false,
    onSocialClick,
    isFBPDP,
    signInLinkProps,
}) {
    const country = environment.get("country");
    const requiresOptIn = country !== "US";

    function generateErrorDiv(message, key) {
        return (
            <div key={key} className={styles.formItemError}>
                <div dangerouslySetInnerHTML={{ __html: message }} />
            </div>
        );
    }

    let errors = {
        email: undefined,
        legal: undefined,
        other: undefined,
    };
    if (formErrors !== undefined) {
        Object.keys(formErrors).map((key) => {
            if (key === "marketing_emails" || key === "privacy_policy") {
                analytics.event(captureType, "sign up", "error_marketing");
                errors.legal = formErrors[key].map((message, id) => generateErrorDiv(message, id));
            } else if (key === "email") {
                analytics.event(captureType, "sign up", "error_invalid_email");
                errors.email = formErrors[key].map((message, id) => generateErrorDiv(message, id));
            } else if (key === "existing_user") {
                analytics.event(captureType, "sign up", "error_existing_user");
                errors.email = formErrors[key].map((message, id) => generateErrorDiv(message, id));
            } else {
                errors.other = formErrors[key].map((message, id) => generateErrorDiv(message, id));
            }
        });
    }

    const email = (
        <fieldset className={styles.fieldset}>
            {!isFBPDP && (
                <label className={styles.label} htmlFor="id_email">
                    {emailLabelText || gettext("account.form.email_label")}
                </label>
            )}
            <input
                type="email"
                name="email"
                value={emailAddress}
                onClick={() => analytics.event(captureType, "fill_field", "field_email")}
                className={styles.input}
                placeholder={gettext("account.form.email_placeholder")}
                autoCapitalize="off"
                onChange={onEmailChange}
                autoFocus
                required
            />
            {errors.email}
        </fieldset>
    );

    const hasEmailAndSocial = !hideEmail && showSocial;

    const social = (
        <div className={styles.socialArea}>
            {hasEmailAndSocial && (
                <span className={styles.orSeparator}>
                    {gettext("in_stock_product.or_separator")}
                </span>
            )}
            <SocialButton
                className={styles.facebookButton}
                type="facebook"
                onClick={() => onSocialClick("facebook")}
                fullWidth
            >
                {gettext("account.register.sign_up_with_facebook")}
            </SocialButton>
            <SocialButton type="google" onClick={() => onSocialClick("google-oauth2")} fullWidth>
                {gettext("account.register.sign_up_with_google")}
            </SocialButton>
        </div>
    );

    const legal = (
        <div className={styles.legalAgreementWrapper}>
            <LegalAgreement
                requiresOptIn={requiresOptIn}
                submitText={submitText}
                handleLegalChange={onLegalChange}
                captureType={captureType}
                privacyPolicyLink={privacyPolicyLink}
                termsAndConditionsLink={termsAndConditionsLink}
                cookiePolicyLink={cookiePolicyLink}
                shortVersion={shortPolicy}
            />
            {errors.legal}
        </div>
    );

    const buttons = (
        <React.Fragment>
            {requiresOptIn && legal}
            {!hideEmail && (
                <Button
                    styleType={"tertiary"}
                    className={classnames(submitClass, styles.submitButton)}
                    type="submit"
                    fullWidth
                >
                    {submitText}
                </Button>
            )}
            {errors.other}
            {showSocial === true && social}
            {!requiresOptIn && legal}
        </React.Fragment>
    );

    const privacyPolicyLinkProps = `href=${privacyPolicyLink} target="_blank"`;
    const termsAndConditionsLinkProps = `href=${termsAndConditionsLink} target="_blank"`;

    const policyText = (
        <div
            className={styles.legalAgreementSmall}
            dangerouslySetInnerHTML={{
                // "By creating an account, you consent to Lyst’s Terms & Conditions.
                // To learn more about how Lyst uses and protects your personal data, please read Lyst’s Privacy Policy."
                __html: gettext("account.register.legal_agreement.sign_up_opt_in_terms", {
                    terms_and_conditions_link_props: termsAndConditionsLinkProps,
                    privacy_policy_link_props: privacyPolicyLinkProps,
                }),
            }}
        />
    );
    let signInText = null;
    if (signInLinkProps) {
        const signInLinkProp = `href=${signInLinkProps}&email=${emailAddress} target="_blank"`;
        signInText = (
            <div
                className={styles.signinBox}
                dangerouslySetInnerHTML={{
                    __html: gettext("account.register.existing_user_sign_in_link_text", {
                        sign_in_link_props: signInLinkProp,
                    }),
                }}
            />
        );
    }

    return (
        <form className={classnames(className, styles.form)} onSubmit={onSubmit}>
            {!hideEmail && email}
            {signInLinkProps && signInText}
            {buttons}
            {requiresOptIn && shortPolicy && policyText}
        </form>
    );
}

export default EmailCaptureForm;

EmailCaptureForm.propTypes = {
    captureType: PropTypes.string.isRequired,
    className: PropTypes.string,
    cookiePolicyLink: PropTypes.string,
    emailAddress: PropTypes.string,
    emailLabelText: PropTypes.string,
    formErrors: PropTypes.object,
    onEmailChange: PropTypes.func.isRequired,
    onLegalChange: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onSocialClick: PropTypes.func,
    showSocial: PropTypes.bool,
    hideEmail: PropTypes.bool,
    privacyPolicyLink: PropTypes.string.isRequired,
    submitText: PropTypes.string.isRequired,
    submitClass: PropTypes.string,
    termsAndConditionsLink: PropTypes.string,
    shortPolicy: PropTypes.bool,
    isFBPDP: PropTypes.bool,
    signInLinkProps: PropTypes.string,
};
